"use client";

import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { Label, Input } from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  AlertTriangle,
  RotateCcw,
  Send,
  Coins,
  Users,
  CheckCircle2,
  Zap,
  Activity,
  ArrowUpRight,
  TrendingUp,
} from "lucide-react";
import { createDecisionAction } from "@/actions/decision-actions";

interface SandboxClientProps {
  baseProjectBudget: number;
  baseOpCosts: number;
  initialBudgetMultiplier?: number;
  initialWorkloadAllocation?: number;
  initialOpexSavings?: number;
  initialNewHires?: number;
}

export function SandboxClient({
  baseProjectBudget,
  baseOpCosts,
  initialBudgetMultiplier = 1.0,
  initialWorkloadAllocation = 100,
  initialOpexSavings = 0,
  initialNewHires = 0,
}: SandboxClientProps) {
  // Sandbox state
  const [budgetMultiplier, setBudgetMultiplier] = useState<number>(
    initialBudgetMultiplier,
  ); // 0.5x to 2.5x
  const [workloadAllocation, setWorkloadAllocation] = useState<number>(
    initialWorkloadAllocation,
  ); // 50% to 150%
  const [opexSavings, setOpexSavings] = useState<number>(initialOpexSavings); // 0% to 50%
  const [newHires, setNewHires] = useState<number>(initialNewHires); // 0 to 10

  // Proposal modal state
  const [isProposalOpen, setIsProposalOpen] = useState(false);
  const [proposalTitle, setProposalTitle] = useState("");
  const [proposalNotes, setProposalNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Base state values
  const cashBalance = 184300;

  // Calculations for baseline
  const baseMonthlyOutflow = baseOpCosts + baseProjectBudget / 6;
  const baselineRunway =
    Math.round((cashBalance / baseMonthlyOutflow) * 10) / 10;
  const baselineBurnout = 60; // 60%
  const baselineWeeks = 12; // 12 weeks to release

  // Calculations for simulated state
  const simulatedProjectBudget = baseProjectBudget * budgetMultiplier;
  const simulatedMonthlyOpCosts =
    baseOpCosts * (1 - opexSavings / 100) + newHires * 5000; // 5k per new hire
  const simulatedMonthlyOutflow =
    simulatedMonthlyOpCosts + simulatedProjectBudget / 6;

  const simulatedRunway =
    Math.round((cashBalance / simulatedMonthlyOutflow) * 10) / 10;

  // Workload and hires speed factor
  const speedFactor =
    (workloadAllocation / 100) * 0.75 + newHires * 0.08 + 0.25;
  const simulatedWeeks = Math.max(4, Math.round(baselineWeeks / speedFactor));

  // Burnout score
  const simulatedBurnout = Math.min(
    100,
    Math.max(
      5,
      Math.round((workloadAllocation - 50) * 0.9 - newHires * 4 + 15),
    ),
  );

  // Delivery confidence
  const simulatedConfidence = Math.min(
    100,
    Math.max(
      20,
      Math.round(
        65 +
          (workloadAllocation > 100
            ? (150 - workloadAllocation) * 0.2
            : (workloadAllocation - 50) * 0.4) +
          newHires * 3 -
          (simulatedRunway < 3 ? (3 - simulatedRunway) * 15 : 0),
      ),
    ),
  );

  const handleReset = () => {
    setBudgetMultiplier(initialBudgetMultiplier);
    setWorkloadAllocation(initialWorkloadAllocation);
    setOpexSavings(initialOpexSavings);
    setNewHires(initialNewHires);
    setSuccessMsg("");
  };

  const handleOpenProposal = () => {
    setProposalTitle("Proposta di Ottimizzazione Strategica");
    setProposalNotes(`Configurazione Sandbox Simulata:
- Moltiplicatore Budget Progetti: ${budgetMultiplier.toFixed(1)}x
- Carico di lavoro team: ${workloadAllocation}%
- Risparmio sui costi operativi: ${opexSavings}%
- Nuove assunzioni pianificate: +${newHires}
- Runway stimato: ${simulatedRunway} mesi (rispetto a ${baselineRunway} mesi baseline)
- Burnout stimato: ${simulatedBurnout}% (rispetto a ${baselineBurnout}% baseline)
- Tempi di rilascio stimati: ${simulatedWeeks} settimane`);
    setSuccessMsg("");
    setIsProposalOpen(true);
  };

  const handleSubmitProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg("");

    try {
      // Create a pending decision representing the proposed sandbox scenario
      const res = await createDecisionAction({
        title: proposalTitle,
        context: proposalNotes,
        verificationDeadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        deciderId: null,
        targetGoalId: null,
        targetKpiId: null,
      });

      if (res.error) {
        alert(res.error);
      } else {
        setSuccessMsg(
          "Proposta inviata con successo! Registrata come decisione pendente nel Ledger.",
        );
        setTimeout(() => {
          setIsProposalOpen(false);
          handleReset();
        }, 2000);
      }
    } catch (err: any) {
      alert("Errore nell'invio della proposta: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getBurnoutBadge = (score: number) => {
    if (score < 45) return <Badge variant="success">Basso ({score}%)</Badge>;
    if (score < 75) return <Badge variant="warning">Moderato ({score}%)</Badge>;
    return <Badge variant="error">Critico ({score}%)</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Sandbox Active Header Alert Banner */}
      <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-4 text-yellow-600 dark:text-yellow-400 flex items-center justify-between gap-4 flex-wrap select-none border-l-4 border-l-yellow-500">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0" />
          <div className="space-y-0.5">
            <span className="text-xs font-bold text-text-primary uppercase tracking-wide">
              Modalità Sandbox Attiva
            </span>
            <p className="text-[11px] text-text-secondary leading-relaxed">
              Stai modificando variabili aziendali simulate. Nessun dato reale
              di produzione verrà sovrascritto.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="h-8 text-xs gap-1 border-yellow-500/20 text-yellow-600 hover:bg-yellow-500/10 cursor-pointer"
          >
            <RotateCcw className="h-3 w-3" /> Ripristina
          </Button>
          <Button
            size="sm"
            onClick={handleOpenProposal}
            className="h-8 text-xs gap-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold cursor-pointer"
          >
            <Send className="h-3 w-3" /> Invia Proposta
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sliders Panel (Left / Col-1 & Col-2) */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-bold">
                Variabili della Simulazione
              </CardTitle>
              <CardDescription>
                Muovi gli slider per simulare variazioni sulle risorse operative
                e finanziarie.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 py-4">
              {/* Budget Multiplier Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs font-semibold">
                  <div className="flex items-center gap-1.5 text-text-primary">
                    <Coins className="h-4 w-4 text-primary-500" />
                    <span>Budget Progetti Attivi</span>
                  </div>
                  <span className="text-primary-600 font-bold bg-primary-500/10 px-2 py-0.5 rounded">
                    {budgetMultiplier.toFixed(1)}x (€
                    {Math.round(
                      baseProjectBudget * budgetMultiplier,
                    ).toLocaleString()}
                    )
                  </span>
                </div>
                <input
                  type="range"
                  min="0.5"
                  max="2.5"
                  step="0.1"
                  value={budgetMultiplier}
                  onChange={(e) =>
                    setBudgetMultiplier(parseFloat(e.target.value))
                  }
                  className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-primary-500 focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-text-muted">
                  <span>Taglio Budget (0.5x)</span>
                  <span>Baseline (1.0x)</span>
                  <span>Espansione (2.5x)</span>
                </div>
              </div>

              {/* Workload Allocation Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs font-semibold">
                  <div className="flex items-center gap-1.5 text-text-primary">
                    <Zap className="h-4 w-4 text-warning" />
                    <span>Allocazione Carico Lavoro Team</span>
                  </div>
                  <span className="text-warning-strong font-bold bg-warning-soft/40 px-2 py-0.5 rounded">
                    {workloadAllocation}%
                  </span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="150"
                  step="5"
                  value={workloadAllocation}
                  onChange={(e) =>
                    setWorkloadAllocation(parseInt(e.target.value))
                  }
                  className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-warning focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-text-muted">
                  <span>Sotto-utilizzato (50%)</span>
                  <span>Bilanciato (100%)</span>
                  <span>Sovraccarico (150%)</span>
                </div>
              </div>

              {/* Operational Cost Reduction Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs font-semibold">
                  <div className="flex items-center gap-1.5 text-text-primary">
                    <Coins className="h-4 w-4 text-success" />
                    <span>Riduzione Costi Fissi / OpEx</span>
                  </div>
                  <span className="text-success font-bold bg-success-soft/30 px-2 py-0.5 rounded">
                    -{opexSavings}% (-€
                    {Math.round(
                      baseOpCosts * (opexSavings / 100),
                    ).toLocaleString()}
                    /mese)
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  step="5"
                  value={opexSavings}
                  onChange={(e) => setOpexSavings(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-success focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-text-muted">
                  <span>Nessun Taglio (0%)</span>
                  <span>Ottimizzazione Media (25%)</span>
                  <span>Taglio Drastico (50%)</span>
                </div>
              </div>

              {/* Hiring Plans Slider */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-xs font-semibold">
                  <div className="flex items-center gap-1.5 text-text-primary">
                    <Users className="h-4 w-4 text-info" />
                    <span>Nuove Assunzioni Operative</span>
                  </div>
                  <span className="text-info font-bold bg-info-soft/30 px-2 py-0.5 rounded">
                    +{newHires} Persone (+€
                    {Math.round(newHires * 5000).toLocaleString()}/mese)
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  value={newHires}
                  onChange={(e) => setNewHires(parseInt(e.target.value))}
                  className="w-full h-1.5 bg-border rounded-lg appearance-none cursor-pointer accent-info focus:outline-none"
                />
                <div className="flex justify-between text-[10px] text-text-muted">
                  <span>Nessuna Assunzione (+0)</span>
                  <span>Espansione Moderata (+5)</span>
                  <span>Forte Assunzione (+10)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projected Impacts & Interactive Graphics Panel (Right / Col-3) */}
        <div className="space-y-6">
          <Card className="h-full flex flex-col justify-between">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-1.5">
                <Activity className="h-4 w-4 text-primary-500 animate-pulse" />
                <CardTitle className="text-base font-bold">
                  Impatto Stimato AI
                </CardTitle>
              </div>
              <CardDescription>
                Proiezioni dell'effetto domino sui principali KPI aziendali.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Cash Runway Impact Graphic */}
              <div className="space-y-3 bg-card-muted/15 border border-border p-4 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-text-primary">
                    Runway di Cassa Stimato
                  </span>
                  <div className="text-right">
                    <span
                      className={`text-xl font-extrabold ${simulatedRunway >= 6 ? "text-success" : simulatedRunway >= 3 ? "text-warning" : "text-error"}`}
                    >
                      {simulatedRunway} mesi
                    </span>
                    <span className="text-[10px] text-text-muted block">
                      Baseline: {baselineRunway} mesi
                    </span>
                  </div>
                </div>

                {/* SVG Visual Bar Chart Comparison */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between text-[10px] font-semibold text-text-secondary">
                    <span>Baseline (Attuale)</span>
                    <span>{baselineRunway} mesi</span>
                  </div>
                  <div className="h-2 w-full bg-border rounded-full overflow-hidden">
                    <div
                      className="bg-text-muted h-full"
                      style={{
                        width: `${Math.min(100, (baselineRunway / 12) * 100)}%`,
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-between text-[10px] font-semibold text-text-secondary">
                    <span>Simulato</span>
                    <span>{simulatedRunway} mesi</span>
                  </div>
                  <div className="h-2 w-full bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full ${simulatedRunway >= 6 ? "bg-success" : simulatedRunway >= 3 ? "bg-warning" : "bg-error"}`}
                      style={{
                        width: `${Math.min(100, (simulatedRunway / 12) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Completion Time */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-text-secondary">
                    Rilascio Milestone Principali
                  </span>
                  <span className="font-bold text-text-primary">
                    ~{simulatedWeeks} sett.
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                    <div
                      className={`h-full ${simulatedWeeks <= baselineWeeks ? "bg-success" : "bg-warning"}`}
                      style={{
                        width: `${Math.min(100, (simulatedWeeks / 20) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-text-muted shrink-0">
                    Baseline: {baselineWeeks} sett.
                  </span>
                </div>
              </div>

              {/* Burnout Risk Gauge */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-text-secondary">
                    Rischio di Burnout Team
                  </span>
                  {getBurnoutBadge(simulatedBurnout)}
                </div>
                <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
                  <div
                    className={`h-full ${simulatedBurnout < 45 ? "bg-success" : simulatedBurnout < 75 ? "bg-warning" : "bg-error"}`}
                    style={{ width: `${simulatedBurnout}%` }}
                  />
                </div>
              </div>

              {/* Delivery Confidence Score */}
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-text-secondary">
                    Fiducia Consegna Obiettivi
                  </span>
                  <span
                    className={`font-bold ${simulatedConfidence >= 75 ? "text-success" : simulatedConfidence >= 50 ? "text-warning" : "text-error"}`}
                  >
                    {simulatedConfidence}%
                  </span>
                </div>
                <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
                  <div
                    className={`h-full ${simulatedConfidence >= 75 ? "bg-success" : simulatedConfidence >= 50 ? "bg-warning" : "bg-error"}`}
                    style={{ width: `${simulatedConfidence}%` }}
                  />
                </div>
              </div>

              {/* Quick AI Sandbox Summary */}
              <div className="pt-2 border-t border-border/60">
                <span className="text-[10px] font-bold text-primary-600 uppercase tracking-wider block mb-1">
                  Feedback AI in tempo reale
                </span>
                <p className="text-[11px] text-text-secondary leading-relaxed bg-primary-500/5 p-3 rounded-lg border border-primary-500/10">
                  {simulatedRunway < 3 ? (
                    <strong className="text-error">
                      Attenzione: La pista di cassa scende sotto la soglia di
                      sicurezza di 3 mesi. Riduci il budget o posticipa le
                      assunzioni.
                    </strong>
                  ) : simulatedBurnout > 75 ? (
                    <strong className="text-error">
                      Attenzione: Il sovraccarico del team supera l'80% di
                      burnout risk. Si raccomanda di dilazionare i rilasci o
                      assumere supporto.
                    </strong>
                  ) : budgetMultiplier > 1.5 && simulatedWeeks <= 8 ? (
                    <span className="text-success font-medium">
                      Scenario Ottimale: L'aumento di budget riduce
                      sensibilmente i tempi di rilascio mantenendo il runway
                      sostenibile.
                    </span>
                  ) : (
                    <span className="text-text-muted">
                      La simulazione mostra un compromesso accettabile tra
                      costi, runway strategico e burnout operativo.
                    </span>
                  )}
                </p>
              </div>
            </CardContent>

            <CardFooter className="pt-4 border-t border-border/30 bg-card-muted/20 px-6 py-4 rounded-b-xl flex items-center justify-between text-xs text-text-muted">
              <span>Modello: Cleos-WhatIf-v1</span>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-success">
                <CheckCircle2 className="h-3.5 w-3.5" /> Calcolo in tempo reale
              </span>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Propose Proposal Dialog */}
      <Dialog open={isProposalOpen} onOpenChange={setIsProposalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invia Proposta Strategica al Board</DialogTitle>
            <DialogDescription>
              Invia lo scenario simulato come proposta per future discussioni.
              Verrà registrato nel Semantic Ledger.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitProposal} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="prop-title">Titolo Proposta</Label>
              <Input
                id="prop-title"
                value={proposalTitle}
                onChange={(e) => setProposalTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="prop-notes">Dettagli della Simulazione</Label>
              <textarea
                id="prop-notes"
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground min-h-[150px] font-mono leading-relaxed"
                value={proposalNotes}
                onChange={(e) => setProposalNotes(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {successMsg && (
              <div className="text-xs font-semibold text-success bg-success-soft/30 p-2.5 rounded border border-success/20">
                {successMsg}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsProposalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={loading}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                Invia al Board
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
